import React from 'react';

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(
    () => {},
    () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    },
  );
};

const buildErrorText = (error, componentStack) => {
  const parts = [
    `Error: ${error?.toString()}`,
    `Page: ${window.location.pathname + window.location.search}`,
    `UA: ${navigator.userAgent}`,
  ];

  if (error?.stack) parts.push(`Stack:\n${error.stack}`);

  if (componentStack) parts.push(`Component Stack:${componentStack}`);

  return parts.join('\n\n');
};

const ErrorBoundaryFallback = ({ error, componentStack }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    copyToClipboard(buildErrorText(error, componentStack));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.icon}>&#9888;</span>
          <span style={styles.title}>Something went wrong</span>
        </div>
        <div style={styles.section}>
          <div style={styles.label}>Error</div>
          <pre style={styles.pre}>{error?.toString()}</pre>
        </div>
        {componentStack && (
          <div style={styles.section}>
            <div style={styles.label}>Component Stack</div>
            <pre style={styles.pre}>{componentStack}</pre>
          </div>
        )}
        <div style={styles.section}>
          <div style={styles.label}>Page</div>
          <pre style={styles.pre}>{window.location.pathname + window.location.search}</pre>
        </div>
        <div style={styles.actions}>
          <button type="button" onClick={handleCopy} style={copied ? styles.btnCopied : styles.btnCopy}>
            {copied ? 'Copied!' : 'Copy Error'}
          </button>
          <button type="button" onClick={() => window.location.reload()} style={styles.btnPrimary}>
            Reload
          </button>
        </div>
      </div>
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return <ErrorBoundaryFallback error={this.state.error} componentStack={this.state.errorInfo?.componentStack} />;
    }

    return this.props.children;
  }
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    overflow: 'auto',
  },
  container: {
    background: '#1a1a2e',
    borderRadius: 12,
    padding: 20,
    maxWidth: 520,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    color: '#e0e0e0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: 14,
    border: '1px solid #ff4d4f',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  icon: {
    fontSize: 28,
    color: '#ff4d4f',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
  },
  section: {
    marginBottom: 14,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#888',
    marginBottom: 4,
  },
  pre: {
    background: '#0d0d1a',
    borderRadius: 6,
    padding: 10,
    margin: 0,
    fontSize: 12,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#ff6b6b',
    maxHeight: 200,
    overflow: 'auto',
    border: '1px solid #2a2a4a',
  },
  actions: {
    display: 'flex',
    gap: 10,
    marginTop: 16,
  },
  btnPrimary: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#ff4d4f',
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  btnCopy: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid #1890ff',
    background: 'transparent',
    color: '#1890ff',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  btnCopied: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid #52c41a',
    background: 'transparent',
    color: '#52c41a',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
};

export { ErrorBoundary, ErrorBoundaryFallback };
